import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Card,
  CardHeader,
  CircularProgress,
  Tooltip,
  Typography,
  IconButton,
  Divider,
  Button,
  styled,
  tooltipClasses,
  TooltipProps,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DashboardWidgetsClient } from "../../../../api/DashboardWidgetsClient";
import { REACT_APP_PORTAL_API_URL } from "../../../../KeyVariables";
import { useTranslation } from "react-i18next";
import { ArrowBackIos, ArrowForwardIos, AttachMoney } from "@mui/icons-material";
import { format } from "date-fns";
import { ReceiptDashboardDTO } from "../../../../api/DashboardWidgetDTO";
import Scrollbars from "react-custom-scrollbars-2";
import { PatientWidgetProps } from "../PatientDashboard";

export const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 500,
  },
});

export const usePreventVerticalScroll = (ref, speed) => {
  useEffect(() => {
    const handleWheelScroll = (event) => {
      if (ref.current && ref.current.contains(event.target)) {
        event.preventDefault();
        event.stopPropagation();

        ref.current.scrollBy({
          left: event.deltaY > 0 ? speed : -speed,
          behavior: "smooth",
        });
      }
    };

    const element = ref.current;
    if (element) {
      element.addEventListener("wheel", handleWheelScroll, { passive: false });
    }

    return () => {
      if (element) {
        element.removeEventListener("wheel", handleWheelScroll);
      }
    };
  }, [ref]);
};


export const PaymentWidget = ({ patientId }: PatientWidgetProps) => {
  const [items, setItems] = useState<ReceiptDashboardDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation("Portal");
  const scrollRefNotPayed = useRef(null);
  const scrollRefPrevious = useRef(null);
  const [loadingServices, setLoadingServices] = useState({});
  const [servicesData, setServicesData] = useState({});
  const iframeRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const data = await new DashboardWidgetsClient(REACT_APP_PORTAL_API_URL).payments(patientId > 0 ? patientId : undefined);
      setItems(data.filter(x => x.price! > 0));
    } catch (error) {
      console.error("Error fetching payment data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchServices = async (id) => {
    if (servicesData[id] || loadingServices[id]) return;
    
    setLoadingServices((prev) => ({ ...prev, [id]: true }));
    try {
      const data = await new DashboardWidgetsClient(REACT_APP_PORTAL_API_URL).paymentsservices(id);
      setServicesData((prev) => ({ ...prev, [id]: data }));
    } catch (error) {
      console.error("Error fetching services:", error);
    } finally {
      setLoadingServices((prev) => ({ ...prev, [id]: false }));
    }
  };

  const mapColor = (statusIs) => {
    switch (statusIs) {
      case 2:
        return "rgba(255, 0, 0, 0.1)";
      case 1:
        return "rgba(0, 255, 0, 0.1)";
      case 4:
        return "rgba(238, 255, 0, 0.1)";
      default:
        return "none";
    }
  };

  const handleScroll = (ref, direction) => {
    if (ref.current) {
      ref.current.scrollBy({ left: direction * 200, behavior: "smooth" });
    }
  };

  const renderPayments = (filterCondition, ref) => {
    usePreventVerticalScroll(ref, 200);

    return (
      <>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6">
            {filterCondition[0] === 2 ? t("Portal_ReceiptsListNotPaid") : t("Portal_ReceiptsListPrevious")}:
          </Typography>
          <IconButton onClick={() => handleScroll(ref, -1)}>
            <ArrowBackIos fontSize="small" />
          </IconButton>
          <Box
            ref={ref}
            sx={{
              display: "flex",
              overflowX: "auto",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
              gap: 1,
              flexGrow: 1,
              scrollBehavior: "smooth",
            }}
          >
            {isLoading ? (
              <CircularProgress size={20} />
            ) : (
              items
                .filter((item) => filterCondition.includes(item.status?.id))
                .map((item, index) => (
                  <CustomWidthTooltip
                    key={index}
                    title={
                      loadingServices[item.id!] ? (
                        <CircularProgress size={20} />
                      ) : (

                            <Box sx={{ p: 1 }}>
                            <Scrollbars autoHeight autoHide autoHeightMax={"30vh"}>

                            <Typography variant="h5">{item.clinic?.name}</Typography>
                            {servicesData[item.id!] && servicesData[item.id!].length > 0 && (
                              <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                                <Box component="tbody">
                                  {servicesData[item.id!].map((s, i) => (
                                    <Box component="tr" key={i}
                                    sx={{ borderBottom: i !== servicesData[item.id!].length - 1 ? "1px solid #ddd" : "none" }}>
                                      <Box component="td" sx={{ pr: 2, textAlign: "left" }}>
                                        {s.name}
                                      </Box>
                                      <Box component="td" sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                                        {s.price?.toFixed(2)} lei
                                      </Box>
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                            )}
                            </Scrollbars>
                          </Box>
                      )
                    }
                    arrow
                    placement="left-start"
                    onOpen={() => fetchServices(item.id)}
                  >
                    <Box
                      sx={{
                        backgroundColor: mapColor(item.status?.id),
                        p: 1,
                        borderRadius: 2,
                        textAlign: "center",
                        minWidth: 100,
                        cursor: "pointer",
                      }}
                    >
                      <Typography>{item.price?.toFixed(2)} lei</Typography>
                      <Typography variant="caption">
                        {item.date && format(item.date, "dd.MM.yyyy")}
                      </Typography>
                    </Box>
                  </CustomWidthTooltip>
                ))
            )}
          </Box>
          <IconButton onClick={() => handleScroll(ref, 1)}>
            <ArrowForwardIos fontSize="small" />
          </IconButton>
        </Box>
        <Divider />
      </>
    );
  };


  return (
    <Card sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <AttachMoney fontSize="medium" />
          <CardHeader title={t("Portal_ReceiptsList")} sx={{ p: 0 }} />
        </Box>
        <Box display="flex" gap={1}>
          <Box sx={{ backgroundColor: mapColor(1), p: 1, borderRadius: 1, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {t("Portal_ReceiptsListPaid")}
          </Box>
          <Box sx={{ backgroundColor: mapColor(2), p: 1, borderRadius: 1, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {t("Portal_ReceiptsListNotPaid")}
          </Box>
          <Box sx={{ backgroundColor: mapColor(4), p: 1, borderRadius: 1, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {t("Portal_ReceiptsListReimbursed")}
          </Box>
        </Box>
      </Box>

      <Box display="flex" flexDirection="column" gap={1}>
        {renderPayments([2], scrollRefNotPayed)}
        {renderPayments([1, 4], scrollRefPrevious)}
      </Box>
      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Button variant="contained" size="small" onClick={() => window.open('/patient-archive?Tab=payments' + (patientId > 0 ? `&PatientId=${patientId}` : ''), '_self')}>
          {t('Portal_ReceiptsListArchive')}
        </Button>
      </Box>
    </Card>
  );
};